'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Image from 'next/image'
import Script from 'next/script'

import { EyeIcon, EyeOffIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { sendEmailMFA, sessionCsrf, sessionLogin, sessionTotpVerify, verifyEmailMFA } from '@/services/auth'

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''

const LoginForm = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [verified, setVerified] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(RECAPTCHA_SITE_KEY)
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const [phase, setPhase] = useState<'creds' | 'enroll' | 'totp' | 'email'>(() => {
    try {
      const p = sessionStorage.getItem('login2fa:phase') as 'creds' | 'enroll' | 'totp' | null
      return p === 'totp' ? 'totp' : 'creds'
    } catch {}
    return 'creds'
  })
  const [qr, setQr] = useState<string | null>(null)
  const [enrollMsg, setEnrollMsg] = useState<string | null>(null)
  const [emailDestination, setEmailDestination] = useState('')
  const [emailPurpose, setEmailPurpose] = useState<'enrollment' | 'login'>('login')
  const [resendSeconds, setResendSeconds] = useState(0)
  const totpInputRef = useRef<HTMLInputElement | null>(null)
  const params = useSearchParams()
  const [persistEmail, setPersistEmail] = useState<string>(() => {
    try {
      const fromSession = sessionStorage.getItem('login2fa:email') || ''
      const fromUrl = (params.get('userEmail') || params.get('email') || '') as string
      return fromSession || String(fromUrl)
    } catch {}
    return ''
  })
  const [persistPassword, setPersistPassword] = useState<string>(() => {
    try {
      const p = sessionStorage.getItem('login2fa:phase')
      if (p === 'totp') return ''
    } catch {}
    return ''
  })
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])
  useEffect(() => {
    if (recaptchaSiteKey) return
    let cancelled = false
    fetch('/api/recaptcha/config/', { cache: 'no-store' })
      .then(response => (response.ok ? response.json() : null))
      .then((payload: { site_key?: string } | null) => {
        const siteKey = String(payload?.site_key || '').trim()
        if (cancelled) return
        if (siteKey) setRecaptchaSiteKey(siteKey)
        else setError('Security verification is unavailable. Please try again later.')
      })
      .catch(() => {
        if (!cancelled) setError('Security verification is unavailable. Please try again later.')
      })
    return () => { cancelled = true }
  }, [recaptchaSiteKey])
  // Ensure CSRF cookie exists as soon as we enter TOTP phase
  useEffect(() => {
    let cancelled = false
    async function ensureCsrf() {
      try {
        if (phase !== 'creds') {
          await sessionCsrf()
        }
      } catch {}
    }
    ensureCsrf()
    return () => { cancelled = true }
  }, [phase])
  // Focus TOTP input whenever we enter TOTP or ENROLL phase
  useEffect(() => {
    try {
      if ((phase === 'totp' || phase === 'enroll' || phase === 'email') && totpInputRef.current) {
        totpInputRef.current.focus()
        totpInputRef.current.select()
      }
    } catch {}
  }, [phase])
  useEffect(() => {
    if (resendSeconds <= 0) return
    const timer = window.setInterval(
      () => setResendSeconds(value => Math.max(0, value - 1)),
      1000,
    )
    return () => window.clearInterval(timer)
  }, [resendSeconds])
  // When entering enrollment phase, fetch QR using preauth
  useEffect(() => {
    let cancelled = false
    async function initEnroll() {
      if (phase !== 'enroll') return
      setEnrollMsg(null)
      setQr(null)
      try {
        const preauth = typeof window !== 'undefined' ? sessionStorage.getItem('login2fa:preauth') : null
        const res = await fetch('/api/auth/2fa/initiate', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(preauth ? { preauth } : {}),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data?.error || 'Unable to initiate 2FA')
        if (data?.already_enabled) {
          if (!cancelled) setEnrollMsg('Two-factor already enabled. Enter your code to sign in.')
          // Fall through to TOTP phase
          if (!cancelled) setPhase('totp')
          return
        }
        if (data?.qr_png_base64) {
          if (!cancelled) setQr(`data:image/png;base64,${data.qr_png_base64}`)
        } else {
          if (!cancelled) setEnrollMsg('Failed to retrieve QR code')
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Unable to start 2FA enrollment')
      }
    }
    initEnroll()
    return () => { cancelled = true }
  }, [phase])
  const router = useRouter()
  // Sanitize URL: strip any userEmail/password query parameters immediately
  useEffect(() => {
    try {
      const emailQ = params.get('userEmail') || params.get('email')
      const pwdQ = params.get('password')
      const rawCbQ = params.get('callbackUrl') || '/'
      const cbQ = rawCbQ && rawCbQ.startsWith('/') ? rawCbQ : '/'
      if (emailQ || pwdQ) {
        const clean = cbQ === '/' ? '/login' : `/login?callbackUrl=${encodeURIComponent(cbQ)}`
        // Replace current URL without adding history
        router.replace(clean)
      }
    } catch {}
  }, [params, router])
  // Only allow same-site absolute paths; root is marketing, so authenticated users land in-app.
  const rawCb = params.get('callbackUrl') || '/'
  const callbackUrl = (rawCb && rawCb.startsWith('/') && rawCb !== '/') ? rawCb : '/dashboard'
  const resendEmailCode = async () => {
    const preauth = typeof window !== 'undefined' ? sessionStorage.getItem('login2fa:preauth') : null
    if (!preauth || resendSeconds > 0) return
    setLoading(true)
    setError(null)
    try {
      const sent = await sendEmailMFA(preauth, emailPurpose)
      setEmailDestination(sent.destination)
      setResendSeconds(sent.resend_after)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Unable to send verification code')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {recaptchaSiteKey ? (
        <Script
          id='tiberius-recaptcha-api'
          src={`https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaSiteKey)}`}
          strategy='afterInteractive'
          onLoad={() => window.grecaptcha?.ready(() => setRecaptchaReady(true))}
          onError={() => setError('Security verification is unavailable. Please try again later.')}
        />
      ) : null}
      <form
      className='space-y-4'
      onSubmit={async e => {
        e.preventDefault()
        if (isMounted.current) setError(null)
        if (isMounted.current) setLoading(true)
        // Clear any stale tokens before pre-login API calls (JWT not used for session flow)
        try { if (typeof window !== 'undefined') { localStorage.removeItem('auth.token'); localStorage.removeItem('auth.refresh') } } catch {}
        const form = e.currentTarget as HTMLFormElement
        const formData = new FormData(form)
        const email = String(formData.get('userEmail') || '').trim()
        const password = String(formData.get('password') || '')
        const totp = String(formData.get('totp') || '').replace(/\D/g, '')
        try {
          if (phase === 'creds') {
            if (!recaptchaSiteKey || !window.grecaptcha || !recaptchaReady) {
              throw new Error('Security verification is not ready. Please try again in a moment.')
            }
            const recaptchaToken = await window.grecaptcha.execute(
              recaptchaSiteKey,
              { action: 'login' },
            )
            await sessionCsrf()
            try {
              const r = await sessionLogin(email, password, recaptchaToken)
              if (r.requires_2fa) {
                const loginPreauth = String(r.preauth || '')
                if (isMounted.current) setPersistEmail(email)
                if (isMounted.current) setPersistPassword(password)
                try {
                  if (loginPreauth) sessionStorage.setItem('login2fa:preauth', loginPreauth)
                  sessionStorage.setItem('login2fa:email', email)
                } catch {}
                if (r.needs_enrollment || (r.has_2fa === false)) {
                  if (r.forced_email_mfa && loginPreauth) {
                    const sent = await sendEmailMFA(loginPreauth, 'enrollment')
                    if (isMounted.current) setEmailDestination(sent.destination)
                    if (isMounted.current) setEmailPurpose('enrollment')
                    if (isMounted.current) setResendSeconds(sent.resend_after)
                    if (isMounted.current) setPhase('email')
                  } else if (isMounted.current) {
                    setPhase('enroll')
                  }
                  if (isMounted.current) setLoading(false)
                  return
                }
                if (r.mfa_method === 'email' && loginPreauth) {
                  const sent = await sendEmailMFA(loginPreauth, 'login')
                  if (isMounted.current) setEmailDestination(sent.destination)
                  if (isMounted.current) setEmailPurpose('login')
                  if (isMounted.current) setResendSeconds(sent.resend_after)
                  if (isMounted.current) setPhase('email')
                  if (isMounted.current) setLoading(false)
                  return
                }
                if (isMounted.current) setPhase('totp')
                try { sessionStorage.setItem('login2fa:phase', 'totp'); sessionStorage.setItem('login2fa:email', email) } catch {}
                if (isMounted.current) setLoading(false)
                return
              }
              // Password accepted and user has NO 2FA enrolled.
              // Backend policy (TWOFA_ENFORCE / TWOFA_DISABLE_FOR) already decides
              // who is required to have 2FA, so do not force-enable here.
              if (!r.has_2fa) {
                if (isMounted.current) setLoading(false)
                if (typeof window !== 'undefined') { window.location.assign(callbackUrl || '/dashboard'); return }
                router.replace(callbackUrl || '/dashboard')
                return
              }
              // Fully authenticated and 2FA not required → proceed
              if (typeof window !== 'undefined') { window.location.assign(callbackUrl || '/dashboard'); return }
              router.replace(callbackUrl || '/dashboard')
            } catch (e: any) {
              if (isMounted.current) setLoading(false)
              if (isMounted.current) setError(e?.message || 'Invalid email or password')
            }
          } else if (phase === 'email') {
            if (totp.length !== 6) {
              if (isMounted.current) setLoading(false)
              if (isMounted.current) setError('Enter the 6-digit code sent to your email')
              return
            }
            const preauth = typeof window !== 'undefined' ? sessionStorage.getItem('login2fa:preauth') : null
            if (!preauth) {
              if (isMounted.current) setLoading(false)
              if (isMounted.current) setError('Session expired. Please sign in again')
              if (isMounted.current) setPhase('creds')
              return
            }
            try {
              await verifyEmailMFA(preauth, emailPurpose, totp)
              try {
                sessionStorage.removeItem('login2fa:preauth')
                sessionStorage.removeItem('login2fa:phase')
                sessionStorage.removeItem('login2fa:email')
              } catch {}
              const destination = emailPurpose === 'enrollment' ? '/login' : (callbackUrl || '/dashboard')
              if (typeof window !== 'undefined') { window.location.assign(destination); return }
              router.replace(destination)
            } catch (e: any) {
              if (isMounted.current) setLoading(false)
              if (isMounted.current) setError(e?.response?.data?.detail || e?.message || 'Invalid or expired code')
            }
          } else if (phase === 'totp') {
            // TOTP phase: require 6 digits, then complete
            if (totp.length !== 6) {
              if (isMounted.current) setLoading(false)
              if (isMounted.current) setError('Enter your 6-digit authentication code')
              return
            }
            if (verified || verifying) {
              // Already processed or in-flight; avoid double-submit
              if (isMounted.current) setLoading(false)
              return
            }
            if (isMounted.current) setVerifying(true)
            // Require stored password only if we don't have a preauth token
            try {
              const preauth = typeof window !== 'undefined' ? sessionStorage.getItem('login2fa:preauth') : null
              if (!preauth && !persistPassword) {
                if (isMounted.current) setLoading(false)
                if (isMounted.current) setError('Session expired. Please re-enter your password')
                if (isMounted.current) setPhase('creds')
                return
              }
            } catch {}
            try {
              // Ensure CSRF cookie present before verifying TOTP
              await sessionCsrf()
              await sessionTotpVerify(totp, persistEmail, persistPassword)
              if (isMounted.current) setVerified(true)
              if (isMounted.current) setLoading(false)
              try { sessionStorage.removeItem('login2fa:phase'); sessionStorage.removeItem('login2fa:email'); sessionStorage.removeItem('login2fa:preauth') } catch {}
              if (typeof window !== 'undefined') { window.location.assign(callbackUrl || '/dashboard'); return }
              router.replace(callbackUrl || '/dashboard')
            } catch (e: any) {
              if (isMounted.current) setLoading(false)
              if (isMounted.current) setError(e?.message || 'Invalid 2FA code')
            } finally {
              if (isMounted.current) setVerifying(false)
            }
          } else if (phase === 'enroll') {
            // Enrollment verify submit
            if (totp.length !== 6) {
              if (isMounted.current) setLoading(false)
              if (isMounted.current) setError('Enter the 6-digit code from your authenticator app')
              return
            }
            try {
              const preauth = typeof window !== 'undefined' ? sessionStorage.getItem('login2fa:preauth') : null
              const res = await fetch('/api/auth/2fa/verify-setup', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(preauth ? { code: totp, preauth } : { code: totp }),
              })
              const data = await res.json().catch(() => ({}))
              if (!res.ok) throw new Error(data?.error || 'Verification failed')
              // On successful enrollment: perform a full logout to restart clean login
              try {
                // Best-effort client cleanup first
                try {
                  sessionStorage.removeItem('login2fa:preauth')
                  sessionStorage.removeItem('login2fa:phase')
                  sessionStorage.removeItem('login2fa:email')
                } catch {}
                try {
                  localStorage.removeItem('auth.token')
                  localStorage.removeItem('auth.refresh')
                } catch {}
                // Server-side logout to clear HttpOnly cookies
                await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {})
                // Attempt to expire common cookies from client side as belt-and-suspenders
                try {
                  const names = ['preauth','csrftoken','sessionid','access','refresh']
                  names.forEach(n => {
                    document.cookie = `${n}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
                  })
                } catch {}
              } catch {}
              if (typeof window !== 'undefined') { window.location.assign('/login'); return }
              router.replace('/login')
            } catch (e: any) {
              if (isMounted.current) setError(e?.message || 'Verification failed')
            } finally {
              if (isMounted.current) setLoading(false)
            }
          }
        } catch (err: any) {
          if (isMounted.current) setLoading(false)
          const msg = (err?.message as string) || 'Unable to sign in. Please try again.'
          if (isMounted.current) setError(msg)
        }
      }}
    >
      {phase === 'creds' && (
        <>
          {/* Email */}
          <div className='space-y-2'>
            <Label className='text-[13px] font-extrabold leading-4 text-[#0A2E5C]' htmlFor='userEmail'>
              Email address
            </Label>
            <Input
              type='email'
              id='userEmail'
              name='userEmail'
              autoComplete='username'
              placeholder='name@company.com'
              className='h-[54px] rounded-[10px] border-[#C9D8E8] bg-[#F8FBFE] px-4 text-[15px] leading-5 text-[#0A2E5C] placeholder:text-[#53677F] focus-visible:ring-[#0F62FE]/30'
            />
          </div>
        </>
      )}

      {phase === 'creds' && (
        <div className='w-full space-y-2'>
          <Label className='text-[13px] font-extrabold leading-4 text-[#0A2E5C]' htmlFor='password'>
            Password
          </Label>
          <div className='relative'>
            <Input
              id='password'
              name='password'
              autoComplete='current-password'
              type={isVisible ? 'text' : 'password'}
              placeholder='••••••••••••'
              className='h-[54px] rounded-[10px] border-[#C9D8E8] bg-[#F8FBFE] px-4 pr-14 text-[15px] leading-5 text-[#0A2E5C] placeholder:text-[#53677F] focus-visible:ring-[#0F62FE]/30'
            />
            <Button
              type='button'
              variant='ghost'
              size='icon'
              onClick={() => setIsVisible(prevState => !prevState)}
              className='absolute inset-y-0 right-1 h-full rounded-l-none text-[#0F62FE] hover:bg-transparent hover:text-[#0A2E5C] focus-visible:ring-[#0F62FE]/30'
            >
              {isVisible ? <EyeOffIcon /> : <EyeIcon />}
              <span className='sr-only'>{isVisible ? 'Hide password' : 'Show password'}</span>
            </Button>
          </div>
        </div>
      )}

      {phase === 'enroll' && (
        <div className='space-y-3'>
          {enrollMsg && <p className='text-sm text-[#53677F]'>{enrollMsg}</p>}
          {qr && (
            <div className='space-y-2'>
              <Label className='text-[13px] font-extrabold leading-4 text-[#0A2E5C]'>Scan this QR code with your authenticator app</Label>
              <Image alt='2FA QR' src={qr} width={200} height={200} className='rounded-md border border-[#D8E4F0]' />
            </div>
          )}
          <div className='space-y-2'>
            <Label className='text-[13px] font-extrabold leading-4 text-[#0A2E5C]' htmlFor='totp'>Authentication code</Label>
            <Input id='totp' name='totp' inputMode='numeric' pattern='[0-9]*' maxLength={6} placeholder='123456' ref={totpInputRef} autoFocus className='h-[54px] rounded-[10px] border-[#C9D8E8] bg-[#F8FBFE] px-4 text-[15px] leading-5 text-[#0A2E5C] placeholder:text-[#53677F] focus-visible:ring-[#0F62FE]/30' />
          </div>
        </div>
      )}

      {phase === 'totp' && (
        <div className='space-y-2'>
          <Label className='text-[13px] font-extrabold leading-4 text-[#0A2E5C]' htmlFor='totp'>Authentication code</Label>
          <Input id='totp' name='totp' inputMode='numeric' pattern='[0-9]*' maxLength={6} placeholder='123456' ref={totpInputRef} autoFocus className='h-[54px] rounded-[10px] border-[#C9D8E8] bg-[#F8FBFE] px-4 text-[15px] leading-5 text-[#0A2E5C] placeholder:text-[#53677F] focus-visible:ring-[#0F62FE]/30' />
        </div>
      )}

      {phase === 'email' && (
        <div className='space-y-3'>
          <p className='text-sm text-[#53677F]'>We sent a code to {emailDestination}. It expires in 3 minutes.</p>
          <div className='space-y-2'>
            <Label className='text-[13px] font-extrabold leading-4 text-[#0A2E5C]' htmlFor='totp'>Email verification code</Label>
            <Input id='totp' name='totp' inputMode='numeric' autoComplete='one-time-code' pattern='[0-9]*' maxLength={6} placeholder='123456' ref={totpInputRef} autoFocus className='h-[54px] rounded-[10px] border-[#C9D8E8] bg-[#F8FBFE] px-4 text-[15px] leading-5 text-[#0A2E5C] placeholder:text-[#53677F] focus-visible:ring-[#0F62FE]/30' />
          </div>
          <Button type='button' variant='ghost' className='w-full' disabled={loading || resendSeconds > 0} onClick={resendEmailCode}>
            {resendSeconds > 0 ? `Send another code in ${resendSeconds}s` : 'Send another code'}
          </Button>
        </div>
      )}

      {/* Remember Me and Forgot Password */}
      <div className='flex items-center justify-between gap-x-4 gap-y-2 pt-2'>
        <div className='flex items-center gap-3'>
          <Checkbox id='rememberMe' className='size-[18px] rounded-[5px] border-[#C9D8E8] data-[state=checked]:border-[#0F62FE] data-[state=checked]:bg-[#0F62FE]' />
          <Label htmlFor='rememberMe' className='text-sm font-bold leading-[18px] text-[#53677F]'>
            {' '}
            Keep me signed in
          </Label>
        </div>

        <a href='/reset-password' className='shrink-0 text-sm font-bold leading-[18px] text-[#0F62FE] hover:underline'>
          Forgot password?
        </a>
      </div>

      {error && (
        <p className='rounded-[10px] border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700'>{error}</p>
      )}

      <Button
        className='h-14 w-full rounded-[10px] bg-[#0F62FE] text-base font-extrabold leading-5 text-white shadow-[0_18px_38px_rgba(15,98,254,0.28)] hover:bg-[#0A56E8] disabled:opacity-70'
        type='submit'
        disabled={loading || (phase === 'creds' && !recaptchaReady)}
      >
        {loading ? 'Please wait…' : phase === 'creds' ? 'Sign in securely' : phase === 'enroll' ? 'Verify & Enable' : 'Verify & Sign in'}
      </Button>
      </form>
    </>
  )
}

export default LoginForm
